import { Typography } from 'design-system/src/components/Typography/Typography';
import Hairline from 'design-system/src/components/Hairline/Hairline';
import Button from 'design-system/src/components/Button/Button';
import { InfoBox } from 'design-system/src/components/InfoBox/InfoBox';
import Link from 'design-system/src/assets/icons/link.svg?react';
import { CopyableDetailsTable } from '../../CopyableDetailsTable';
import { getEhrIntegrationConfig } from '../../tasks/EhrIntegrationConfig';
import type { EhrIntegrationFormProps } from './EhrIntegrationForm';
import { InstructionsFooter } from './InstructionsFooter';
import { InstructionsTitle } from './InstructionsTitle';
import { FormContainer, BodyContainer } from './styles';

/**
 * EHR integration form component for nextgen
 */
export const NextGenInstructions = ({ className, ehrType, practiceName }: EhrIntegrationFormProps) => {
  const config = getEhrIntegrationConfig(ehrType, practiceName);
  const ehrDisplayName = config.ehrName;

  return (
    <FormContainer className={className}>
      <InstructionsTitle ehrDisplayName={ehrDisplayName} titleText="Connect with NextGen" />
      <Hairline className="!my-8" />
      <BodyContainer>
        <ol className="m-0 flex w-full list-none flex-col gap-8 p-0">
          <li className="flex flex-col gap-3">
            <Typography weight={600}>1. Verify API Suite Manager Installation</Typography>

            <Typography>
              Please confirm with your IT team or NextGen support that the API Suite Manager is installed and configured
              in your NGE production environment.
              <Button
                variant="link"
                href="https://hs-50110428.f.hubspotemail.net/hubfs/50110428/NextGen%20API%20Guidelines.pdf"
                className="!inline-flex !items-center !gap-1 !text-purple-700 hover:!text-purple-900"
                target="_blank"
                rel="noopener noreferrer"
              >
                View guidelines
                <Link title="opens in a new tab" />
              </Button>
            </Typography>

            <Typography>
              <span className="font-bold">Note:</span> This software package is typically already installed, but NextGen
              requires us to verify it's included before integration can proceed.
            </Typography>
          </li>
          <li className="flex flex-col gap-3">
            <Typography weight={600}>2. Send consent email to NextGen</Typography>

            <Typography>
              NextGen requires an email sent directly from your organization to APIPartners@nextgen.com to formally
              provide consent for Tandem to integrate with your NGE environments.
            </Typography>

            <InfoBox variant="surface" noTopMargin noBottomMargin>
              <Typography styledAs="bodySmallSpaceGrotesk" weight={500}>
                This email must come from your organization's email address.
              </Typography>
              <Typography styledAs="bodySmallDMSans">
                NextGen requires an email sent from your organization to formally provide consent for Tandem to
                integrate with your practice. Please CC{' '}
                <Button
                  variant="link"
                  href="mailto:provider-support@withtandem.com"
                  className="!text-purple-700 hover:!text-purple-900"
                >
                  provider-support@withtandem.com
                </Button>{' '}
                so we can track the response to complete the setup.
              </Typography>
            </InfoBox>

            <CopyableDetailsTable
              rows={[
                {
                  label: 'To',
                  value: 'APIPartners@nextgen.com',
                  showCopyButton: true,
                },
                {
                  label: 'CC',
                  value: 'provider-support@withtandem.com',
                  showCopyButton: true,
                },
                {
                  label: 'Subject',
                  value: 'API Integration Consent for Tandem',
                  showCopyButton: true,
                },
                {
                  label: 'Body',
                  value: (
                    <div>
                      {practiceName} has agreed to adopt an API-powered integration of the Tandem application, and
                      affirms {practiceName} has entered into an appropriate agreement with Tandem concerning the
                      adoption of this app.
                      <br />
                      <br />
                      We have verified the required API license update & NextGen API Suite Manager software installation
                      & configuration (as described in the NextGen Enterprise API Success Community article) has
                      occurred in the following NextGen Enterprise® environments, which are each within the scope of the
                      Tandem integration (for pre-Production testing and/or Production use):
                      <ul className="my-2 list-disc pl-6">
                        <li>Dev NGE Environment</li>
                        <li>Prod NGE Environment</li>
                      </ul>
                      <br />
                      Additionally, {practiceName} gives consent for NextGen Healthcare to securely communicate the
                      unique API identifier for each environment listed above to Tandem, which will enable the Tandem
                      app to use NextGen APIs to obtain and modify data in the database(s) of each of the environments
                      named above.
                      <br />
                      <br />
                      Finally, if non-Production databases are in use, we understand that said TEST/DEMO/DEVL
                      environment potentially contains PHI or Protected Health Information, and if NextGen Enterprise®
                      Advanced Audit is not enabled in the aforementioned environment, Tandem will have access that
                      cannot be monitored.
                      <br />
                      <br />
                      We acknowledge that we may revoke this consent on a per-environment basis at any time via request
                      to APIPartners@nextgen.com.
                    </div>
                  ),
                  copyValue: `${practiceName} has agreed to adopt an API-powered integration of the Tandem application, and affirms ${practiceName} has entered into an appropriate agreement with Tandem concerning the adoption of this app.

We have verified the required API license update & NextGen API Suite Manager software installation & configuration (as described in the NextGen Enterprise API Success Community article) has occurred in the following NextGen Enterprise® environments, which are each within the scope of the Tandem integration (for pre-Production testing and/or Production use):
- Dev NGE Environment
- Prod NGE Environment

Additionally, ${practiceName} gives consent for NextGen Healthcare to securely communicate the unique API identifier for each environment listed above to Tandem, which will enable the Tandem app to use NextGen APIs to obtain and modify data in the database(s) of each of the environments named above.

Finally, if non-Production databases are in use, we understand that said TEST/DEMO/DEVL environment potentially contains PHI or Protected Health Information, and if NextGen Enterprise® Advanced Audit is not enabled in the aforementioned environment, Tandem will have access that cannot be monitored.

We acknowledge that we may revoke this consent on a per-environment basis at any time via request to APIPartners@nextgen.com.`,
                  showCopyButton: true,
                  fullWidthValue: true,
                },
              ]}
            />
          </li>
          <li className="flex flex-col gap-3">
            <Typography weight={600}>3. Tandem will complete integration</Typography>

            <Typography>
              Once you send this email to NextGen, they will communicate your unique API identifiers to Tandem, and
              we'll complete the integration setup on our end.
            </Typography>
          </li>
        </ol>
      </BodyContainer>
      <InstructionsFooter />
    </FormContainer>
  );
};
